import { AWARDS_API, fetchWithAuth } from '../utils/api';

/**
 * Service for managing scoring rules integration between awards settings and competitions
 */
class ScoringRulesService {
  constructor() {
    this.cachedRules = null;
    this.lastFetchTime = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get scoring rules from awards settings
   * @returns {Promise<Object>} Scoring rules configuration
   */
  async getScoringRules() {
    try {
      // Check cache first
      if (this.cachedRules && this.lastFetchTime && 
          (Date.now() - this.lastFetchTime) < this.cacheTimeout) {
        console.log('Returning cached scoring rules');
        return this.cachedRules;
      }

      console.log('Fetching scoring rules from awards API');
      const awardsData = await fetchWithAuth(AWARDS_API);
      
      const scoringRules = awardsData.scoringRules || {};
      
      // Cache the results
      this.cachedRules = scoringRules;
      this.lastFetchTime = Date.now();
      
      console.log('Retrieved scoring rules:', scoringRules);
      return scoringRules;
    } catch (error) {
      console.error('Failed to fetch scoring rules:', error);
      
      // Return cached rules if available, otherwise return empty rules
      return this.cachedRules || {};
    }
  }

  /**
   * Apply scoring rules to a competition via polling API
   * @param {string} competitionCode - Competition code
   * @returns {Promise<boolean>} Success status
   */
  async applyScoringRulesToCompetition(competitionCode) {
    try {
      const scoringRules = await this.getScoringRules();
      
      if (!scoringRules || Object.keys(scoringRules).length === 0) {
        console.log('No scoring rules configured, skipping application');
        return true;
      }

      console.log(`Applying scoring rules to competition ${competitionCode}:`, scoringRules);
      
      // Get API base URL from environment or use default
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
      const endpoint = `${API_BASE_URL}/competition/${encodeURIComponent(competitionCode)}/scoring-rules`;
      
      console.log(`📡 Posting scoring rules to: ${endpoint}`);
      
      // Get authentication token
      const { tokenManager } = await import('../utils/tokenManager');
      const token = await tokenManager.getToken();
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ scoringRules })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to apply scoring rules: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log(`✅ Scoring rules applied successfully:`, result);
      
      return true;
    } catch (error) {
      console.error('Failed to apply scoring rules to competition:', error);
      return false;
    }
  }

  /**
   * Clear cached scoring rules (call when rules are updated)
   */
  clearCache() {
    this.cachedRules = null;
    this.lastFetchTime = null;
    console.log('Scoring rules cache cleared');
  }

  /**
   * Get scoring rules for display in judge interface
   * @returns {Promise<Object>} Formatted scoring rules for display
   */
  async getScoringRulesForDisplay() {
    const rules = await this.getScoringRules();
    
    if (!rules || Object.keys(rules).length === 0) {
      return null;
    }

    // Format rules for display
    return {
      ...rules,
      displaySummary: this.generateDisplaySummary(rules)
    };
  }

  /**
   * Generate a human-readable summary of scoring rules
   * @param {Object} rules - Scoring rules configuration
   * @returns {string} Display summary
   */
  generateDisplaySummary(rules) {
    const summaryParts = [];

    if (rules.minJudges || rules.maxJudges) {
      summaryParts.push(`${rules.minJudges || 1}-${rules.maxJudges || 4} judges`);
    }

    if (rules.dropLowestScore) {
      summaryParts.push('Drop lowest score');
    }

    if (rules.tieBreakingRules && rules.tieBreakingRules.length > 0) {
      summaryParts.push('Tie-breaking rules active');
    }

    if (rules.missingJudgeRules && rules.missingJudgeRules.length > 0) {
      summaryParts.push('Missing judge handling');
    }

    return summaryParts.length > 0 
      ? summaryParts.join(' • ')
      : 'Standard scoring rules';
  }

  /**
   * Validate scoring rules configuration
   * @param {Object} rules - Scoring rules to validate
   * @returns {Object} Validation result
   */
  validateScoringRules(rules) {
    const errors = [];
    const warnings = [];

    if (!rules || typeof rules !== 'object') {
      errors.push('Scoring rules must be an object');
      return { valid: false, errors, warnings };
    }

    // Validate judge counts
    if (rules.minJudges && rules.maxJudges && rules.minJudges > rules.maxJudges) {
      errors.push('Minimum judges cannot be greater than maximum judges');
    }

    if (rules.minJudges && rules.minJudges < 1) {
      errors.push('Minimum judges must be at least 1');
    }

    if (rules.maxJudges && rules.maxJudges > 10) {
      warnings.push('More than 10 judges may impact performance');
    }

    // Validate target total score
    if (rules.targetTotalScore && rules.targetTotalScore <= 0) {
      errors.push('Target total score must be positive');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// Export singleton instance
export const scoringRulesService = new ScoringRulesService();
export default scoringRulesService;
